import { useEffect, useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { api, type ModelInputs, type MarginalBinding } from '../lib/api';
import { Card, CardHeader, Table, Th, Tr, Td, Badge, ActionButton, Spinner, EmptyState, MetricCard } from '../components/ui';
import { fmtCurrencyWhole, fmtPct } from '../lib/fmt';

// ── Client mirrors of the (server-tested) coupling + forecast, so the steppers
//    drive marginal AND time-phased forecast live, no round-trips. Canonical
//    logic lives in services/marginalModel + services/timePhasedModel. ──
type MState = { ics: number; dms: number; sas: number; reps: number };
type MParams = ModelInputs['params'];
const round = (n: number) => Math.round(n * 100) / 100;
const ym = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

function evalPoint(s: MState, p: MParams) {
  const caps: number[] = [];
  if (s.dms > 0 && p.avg_ics_per_dm) caps.push(s.dms * p.avg_ics_per_dm);
  if (s.sas > 0 && p.avg_ics_per_sa) caps.push(s.sas * p.avg_ics_per_sa);
  const cap = caps.length ? Math.min(...caps) : Infinity;
  const eff = Math.min(s.ics, cap);
  const supply = eff * p.rev_per_ic + s.dms * p.rev_per_dm + s.sas * p.rev_per_sa + (p.other_production_baseline ?? 0);
  const demand = s.reps * p.bookings_per_rep * (p.uplift ?? 1); // deals logged low → uplift to billings
  const revenue = Math.min(supply, demand);
  const binding: MarginalBinding = supply < demand ? 'delivery' : supply > demand ? 'sales' : 'balanced';
  const fteCost = p.fte_cost_per_head ?? 0;
  const fteHeads = Math.max(0, s.ics + s.dms + s.sas - (p.contractor_heads ?? 0));
  const cost = fteHeads * fteCost + s.reps * (p.rep_cost_per_head ?? fteCost) + (p.contractor_base_cost ?? 0);
  const profit = revenue - cost;
  return { supply, demand, revenue, binding, cost, profit, margin: revenue > 0 ? profit / revenue : null, effective_ics: cap === Infinity ? s.ics : eff, max_ics: cap === Infinity ? null : cap };
}
const ROLES = ['sales', 'IC', 'DM', 'SA'] as const;
const roleKey: Record<(typeof ROLES)[number], keyof MState> = { sales: 'reps', IC: 'ics', DM: 'dms', SA: 'sas' };
function leversFrom(state: MState, p: MParams) {
  const base = evalPoint(state, p);
  return ROLES.map((role) => {
    const bump = (d: number) => { const s = { ...state, [roleKey[role]]: Math.max(0, state[roleKey[role]] + d) }; return evalPoint(s, p); };
    const a = bump(+1), s = bump(-1);
    return { role, add: { d: round(a.revenue - base.revenue), dp: round(a.profit - base.profit), b: a.binding }, sub: { d: round(s.revenue - base.revenue), dp: round(s.profit - base.profit), b: s.binding } };
  });
}
type FBind = 'capacity' | 'demand' | 'balanced';
function forecast(inp: ModelInputs, sim: MState, o: { lag: number; duration: number; uplift: number; horizon: number }) {
  const now = new Date();
  const months: string[] = [];
  for (let i = 0; i < o.horizon; i++) months.push(ym(new Date(now.getFullYear(), now.getMonth() + i, 1)));
  // Deal value by close month: recent won (past, still billing) + pipeline (future);
  // each spreads uplifted billings over the engagement duration, billed as worked.
  const dealVal = new Map<string, number>();
  for (const w of inp.won_by_month) dealVal.set(w.month, (dealVal.get(w.month) ?? 0) + w.amount);
  for (const p of inp.pipeline_by_month) dealVal.set(p.month, (dealVal.get(p.month) ?? 0) + p.expected);
  const mIdx = (m: string) => { const [y, mo] = m.split('-').map(Number); return y! * 12 + (mo! - 1); };
  const monthFrom = (i: number) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`;
  const nowIdx = mIdx(months[0]!);
  const dealAt = (offset: number) => dealVal.get(monthFrom(nowIdx + offset)) ?? 0;
  const pt = evalPoint(sim, inp.params);
  const lag = Math.max(0, Math.trunc(o.lag)), duration = Math.max(1, Math.trunc(o.duration));
  const uplift = o.uplift > 0 ? o.uplift : 1;
  let backlog = 0;
  return months.map((month, t) => {
    let dealSum = 0;
    for (let k = lag; k < lag + duration; k++) dealSum += dealAt(t - k);
    const demandIn = dealSum * uplift / duration;
    const demand = demandIn + backlog, cap = pt.supply, recognized = Math.min(demand, cap);
    backlog = round(demand - recognized);
    const binding: FBind = recognized + 0.5 < demand ? 'capacity' : demandIn + backlog + 0.5 < cap ? 'demand' : 'balanced';
    return { month, demand_in: round(demandIn), demand: round(demand), capacity: round(cap), recognized: round(recognized), profit: round(recognized - pt.cost), backlog, binding };
  });
}

const bindBadge = (b: MarginalBinding) =>
  <Badge variant={b === 'delivery' ? 'amber' : b === 'sales' ? 'blue' : 'green'}>{b}-bound</Badge>;
const fBindBadge = (b: FBind) => <Badge variant={b === 'capacity' ? 'amber' : b === 'demand' ? 'blue' : 'green'}>{b}</Badge>;
const delta = (n: number) => (n > 0 ? `+${fmtCurrencyWhole(n)}` : n < 0 ? `−${fmtCurrencyWhole(-n)}` : '—');
const sel: React.CSSProperties = { fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };
const hdr: React.CSSProperties = { fontSize: 13, fontWeight: 500 };

/**
 * Numeric box that keeps what you typed.
 *
 * WHY A DRAFT STRING. Bound straight to a number - `value={v}` with
 * `onChange={Number(...)}` - the text "1." parses to 1, React rewrites the
 * box to "1", and the decimal point is eaten the moment it is typed: 1.2
 * could never be edited into 1.25. Clearing the box was worse, because an
 * empty string parsed to 0 and then got clamped straight back up to `min`.
 * The draft holds the literal text; the parent hears only about values that
 * actually parse and respect `min`, and the draft is dropped on blur so the
 * box re-syncs with whatever the parent settled on.
 */
function NumBox({ value, onChange, min, step, width }: {
  value: number; onChange: (n: number) => void; min: number; step?: number; width: number;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input type="number" min={min} step={step} value={draft ?? String(value)}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const n = Number(raw);
        if (raw.trim() !== '' && Number.isFinite(n) && n >= min) onChange(n);
      }}
      onBlur={() => setDraft(null)}
      style={{ ...sel, width }} />
  );
}

export default function AdminFpaPredictiveModelPage() {
  const [months, setMonths] = useState(3);
  return (
    <>
      <Card>
        <CardHeader>
          <div style={hdr}>Predictive model</div>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>look back</span>
            <select value={months} onChange={(e) => setMonths(Number(e.target.value))} style={sel}
              title="How much history feeds the run-rates. Recency weighting already skews toward the current month within the window — a shorter window ≈ current run-rate, a longer one blends in more history.">
              {[3, 6, 9, 12, 18, 24, 36].map((m) => <option key={m} value={m}>Last {m} mo</option>)}
            </select>
          </span>
        </CardHeader>
        <div style={{ padding: '6px 16px', fontSize: 11, color: 'var(--color-text-muted)', borderBottom: '0.5px solid var(--color-border)' }}>
          Revenue is bounded by both what you can <strong>sell</strong> (demand) and <strong>deliver</strong> (supply):
          {' '}<code>revenue = min(supply, demand)</code>. The steppers below drive the marginal model AND the forecast live.
          All inputs are recency-weighted toward current state.
        </div>
      </Card>
      <ScenarioSection months={months} />
      <RoleEconSection months={months} />
      <SalesSection months={months} />
    </>
  );
}

function ScenarioSection({ months }: { months: number }) {
  const { data, isLoading, error } = useQuery(() => api.fpa.modelInputs(months), [months]);
  const [sim, setSim] = useState<MState | null>(null);
  const [lag, setLag] = useState(0);
  const [duration, setDuration] = useState(4);
  const [uplift, setUplift] = useState(1.2);
  const [horizon, setHorizon] = useState(12);
  useEffect(() => { if (data) { setSim({ ...data.base_state }); setLag(data.defaults.lag_months); setDuration(data.defaults.duration_months); setUplift(data.defaults.uplift); setHorizon(data.defaults.horizon_months); } }, [data]);

  if (isLoading) return <Card><CardHeader><div style={hdr}>Scenario</div></CardHeader><Spinner /></Card>;
  if (error || !data || !sim) return <Card><CardHeader><div style={hdr}>Scenario</div></CardHeader><EmptyState message={error ?? 'No data'} /></Card>;

  const base = evalPoint(data.base_state, data.params);
  const cur = evalPoint(sim, data.params);
  const dRev = cur.revenue - base.revenue;
  const changed = (['ics', 'dms', 'sas', 'reps'] as const).some((k) => sim[k] !== data.base_state[k]);
  const step = (k: keyof MState, d: number) => setSim((s) => s ? { ...s, [k]: Math.max(0, s[k] + d) } : s);
  const levers = leversFrom(sim, data.params);
  const series = forecast(data, sim, { lag, duration, uplift, horizon });
  const num = (v: number, set: (n: number) => void, min: number) => <NumBox value={v} onChange={set} min={min} width={44} />;
  const numF = (v: number, set: (n: number) => void) => <NumBox value={v} onChange={set} min={1} step={0.05} width={50} />;
  // A HELPER, not a component: declared in here, a component is a new type on
  // every render and React remounts it, so the +/- button under the cursor
  // loses focus on each click. Returning the element keeps the type stable.
  const stepper = (label: string, k: keyof MState) => (
    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
      <span style={{ width: 64, fontSize: 12 }}>{label}</span>
      <ActionButton onClick={() => step(k, -1)}>−</ActionButton>
      <span style={{ minWidth: 28, textAlign: 'center', fontSize: 13, fontWeight: 600 }}>{sim[k]}</span>
      <ActionButton onClick={() => step(k, +1)}>+</ActionButton>
      {data.base_state[k] !== sim[k] && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>(was {data.base_state[k]})</span>}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div style={hdr}>Scenario — add / subtract a role</div>
        {changed && <span style={{ marginLeft: 'auto' }}><ActionButton onClick={() => setSim({ ...data.base_state })}>Reset</ActionButton></span>}
      </CardHeader>

      <div style={{ display: 'flex', gap: 8, padding: '10px 16px', flexWrap: 'wrap' }}>
        <MetricCard label="Deliverable supply" value={fmtCurrencyWhole(cur.supply)} sub={`${cur.effective_ics} effective ICs${cur.max_ics != null ? ` / ${cur.max_ics} cap` : ''}`} accent={cur.binding === 'delivery' ? 'amber' : undefined} />
        <MetricCard label="Sellable demand" value={fmtCurrencyWhole(cur.demand)} sub={`${sim.reps} reps`} accent={cur.binding === 'sales' ? 'amber' : undefined} />
        <MetricCard label="Modelled revenue / mo" value={fmtCurrencyWhole(cur.revenue)} sub={`vs baseline ${delta(dRev)}`} accent={dRev > 0 ? 'green' : 'blue'} />
        <MetricCard label="Profit / mo" value={fmtCurrencyWhole(cur.profit)} sub={`margin ${cur.margin == null ? '—' : fmtPct(cur.margin)} · vs base ${delta(cur.profit - base.profit)}`} accent={cur.profit > base.profit ? 'green' : undefined} />
        <MetricCard label="Binding constraint" value={bindBadge(cur.binding)} sub="what's holding you back" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', padding: '4px 16px 12px' }}>
        {stepper('Sales', 'reps')}{stepper('IC', 'ics')}{stepper('DM', 'dms')}{stepper('SA', 'sas')}
      </div>

      <div style={{ padding: '0 16px 14px' }}>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>Marginal effect of ±1 head (from current scenario):</div>
        <Table>
          <thead><tr><Th>Role</Th><Th right>Add +1: Δrev</Th><Th right>Δprofit</Th><Th>bound by</Th><Th right>Remove −1: Δprofit</Th><Th>bound by</Th></tr></thead>
          <tbody>
            {levers.map((l) => (
              <Tr key={l.role}>
                <Td bold>{l.role === 'sales' ? 'Sales rep' : l.role}</Td>
                <Td right muted>{delta(l.add.d)}</Td>
                <Td right style={{ color: l.add.dp > 0 ? 'var(--color-green)' : l.add.dp < 0 ? 'var(--color-red-text)' : undefined }}>{delta(l.add.dp)}</Td>
                <Td>{bindBadge(l.add.b)}</Td>
                <Td right style={{ color: l.sub.dp > 0 ? 'var(--color-green)' : undefined }}>{delta(l.sub.dp)}</Td>
                <Td>{bindBadge(l.sub.b)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div style={{ padding: '8px 16px 4px', borderTop: '0.5px solid var(--color-border)', display: 'flex', alignItems: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 500 }}>Time-phased forecast (this scenario)</div>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 11, color: 'var(--color-text-muted)' }}>
          uplift {numF(uplift, setUplift)}× · spread over {num(duration, setDuration, 1)} mo · lag {num(lag, setLag, 0)} mo · horizon {num(horizon, setHorizon, 1)} mo
        </span>
      </div>
      <div style={{ padding: '6px 16px', fontSize: 11, color: 'var(--color-text-muted)', borderBottom: '0.5px solid var(--color-border)' }}>
        Deals (won + pipeline × probability) bill at <strong>{uplift.toFixed(2)}×</strong> their logged value, spread over the engagement
        duration, billed as worked. Monthly revenue = min(billings demand, capacity).
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '8px 16px', flexWrap: 'wrap' }}>
        <MetricCard label="Forecast billings (horizon)" value={fmtCurrencyWhole(series.reduce((s, m) => s + m.recognized, 0))} sub={`${horizon} mo, this scenario`} accent="green" />
        <MetricCard label="Forecast profit (horizon)" value={fmtCurrencyWhole(series.reduce((s, m) => s + m.profit, 0))} sub="billings − cost" accent="green" />
      </div>
      <Table>
        <thead><tr><Th>Month</Th><Th right>Billings in</Th><Th right>Demand</Th><Th right>Capacity</Th><Th right>Billed</Th><Th right>Profit</Th><Th right>Backlog</Th><Th>Bound by</Th></tr></thead>
        <tbody>
          {series.map((m) => (
            <Tr key={m.month}>
              <Td bold>{m.month}</Td>
              <Td right muted>{fmtCurrencyWhole(m.demand_in)}</Td>
              <Td right muted>{fmtCurrencyWhole(m.demand)}</Td>
              <Td right muted>{fmtCurrencyWhole(m.capacity)}</Td>
              <Td right>{fmtCurrencyWhole(m.recognized)}</Td>
              <Td right style={{ color: m.profit < 0 ? 'var(--color-red-text)' : undefined }}>{fmtCurrencyWhole(m.profit)}</Td>
              <Td right style={{ color: m.backlog > 0 ? 'var(--color-amber)' : undefined }}>{fmtCurrencyWhole(m.backlog)}</Td>
              <Td>{fBindBadge(m.binding)}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function RoleEconSection({ months }: { months: number }) {
  const { data, isLoading, error } = useQuery(() => api.fpa.roleEconomics(months), [months]);
  return (
    <Card>
      <CardHeader><div style={hdr}>Role economics</div>
        {data && <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-muted)' }}>recency half-life {data.half_life_months} mo</span>}
      </CardHeader>
      {isLoading ? <Spinner /> : error || !data ? <EmptyState message={error ?? 'No data'} /> : (
        <Table>
          <thead><tr><Th>Role</Th><Th right>Heads</Th><Th right>FTE / Contr</Th><Th right>Direct billing / head</Th><Th right>Contractor cost / mo</Th><Th right>Utilisation</Th><Th right>Hours / mo</Th><Th right>ICs / mgr</Th><Th right>Engagements</Th></tr></thead>
          <tbody>
            {data.by_role.map((r) => (
              <Tr key={r.role}>
                <Td bold>{r.role}</Td>
                <Td right>{r.heads}</Td>
                <Td right muted>{r.heads_fte} / {r.heads_contractor}</Td>
                <Td right>{r.avg_direct_billing_per_month == null ? '—' : fmtCurrencyWhole(r.avg_direct_billing_per_month)}</Td>
                <Td right muted>{r.contractor_cost_per_month > 0 ? fmtCurrencyWhole(r.contractor_cost_per_month) : '—'}</Td>
                <Td right>{r.avg_utilisation == null ? '—' : fmtPct(r.avg_utilisation)}</Td>
                <Td right>{r.avg_logged_hours_per_month ?? '—'}</Td>
                <Td right>{r.avg_ics_supported ?? '—'}</Td>
                <Td right>{r.avg_engagements ?? '—'}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function SalesSection({ months }: { months: number }) {
  const { data, isLoading, error } = useQuery(() => api.fpa.salesCapacity(months), [months]);
  if (isLoading) return <Card><CardHeader><div style={hdr}>Sales capacity</div></CardHeader><Spinner /></Card>;
  if (error || !data) return <Card><CardHeader><div style={hdr}>Sales capacity</div></CardHeader><EmptyState message={error ?? 'No data'} /></Card>;
  const t = data.team;
  return (
    <Card>
      <CardHeader><div style={hdr}>Sales capacity &amp; demand</div></CardHeader>
      <div style={{ display: 'flex', gap: 8, padding: '10px 16px', flexWrap: 'wrap' }}>
        <MetricCard label="Active reps" value={t.active_reps} sub={`${t.productive_reps} with wins${data.reps.length > t.active_reps ? ` · ${data.reps.length - t.active_reps} former` : ''}`} />
        <MetricCard label="Avg bookings / rep / mo" value={t.avg_bookings_per_rep == null ? '—' : fmtCurrencyWhole(t.avg_bookings_per_rep)} sub="marginal seller value" accent="blue" />
        <MetricCard label="Win rate" value={t.avg_win_rate == null ? '—' : fmtPct(t.avg_win_rate)} sub={t.avg_deal_size == null ? undefined : `avg deal ${fmtCurrencyWhole(t.avg_deal_size)}`} />
        <MetricCard label="Open pipeline (weighted)" value={fmtCurrencyWhole(t.open_pipeline_weighted)} sub={t.avg_sales_cycle_days == null ? undefined : `${t.avg_sales_cycle_days}d cycle`} accent="green" />
      </div>
      {data.reps.length === 0 ? <EmptyState message="No HubSpot deal activity in window." /> : (
        <Table>
          <thead><tr><Th>Rep</Th><Th>ORP user</Th><Th right>Bookings / mo</Th><Th right>Won</Th><Th right>Win rate</Th><Th right>Avg deal</Th><Th right>Cycle (d)</Th><Th right>Open pipeline</Th></tr></thead>
          <tbody>
            {data.reps.map((r) => (
              <Tr key={r.hubspot_owner_id}>
                <Td bold>
                  {r.owner_name ?? r.email ?? r.hubspot_owner_id}
                  {r.archived && (
                    <span style={{ marginLeft: 6 }} title="Deactivated in HubSpot — counted for past sales, not current capacity">
                      <Badge variant="gray">former</Badge>
                    </span>
                  )}
                </Td>
                <Td muted>{r.user_jira_id ?? <span title="HubSpot email didn't match an ORP user">unlinked</span>}</Td>
                <Td right>{r.bookings_per_month == null ? '—' : fmtCurrencyWhole(r.bookings_per_month)}</Td>
                <Td right>{r.won_count}</Td>
                <Td right>{r.win_rate == null ? '—' : fmtPct(r.win_rate)}</Td>
                <Td right>{r.avg_deal_size == null ? '—' : fmtCurrencyWhole(r.avg_deal_size)}</Td>
                <Td right>{r.sales_cycle_days ?? '—'}</Td>
                <Td right>{fmtCurrencyWhole(r.open_pipeline_weighted)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
